import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseBudgetItemTypeEnum,
  type SimpleTaskForClientFragment,
  type SimpleTaskFragment,
  type Task,
  TaskSystemStatusEnum,
} from "@src/__generated__/graphql";
import { OurWorkTooltipLabel } from "@src/components/modules/projects/detail/ProgressBar/OurWork";
import type { InternalTaskType } from "@src/components/modules/resource-planning/tasks/listing/store";
import { Icon } from "@src/components/ui-kit/Icon";
import { TaskItemProject } from "@src/components/ui-kit/TaskItem/TaskItemProject";
import type { TaskModel } from "@src/components/widgets/Modals/ModalCommunication/models";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, {
  type FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { OurWorkOverviewModel } from "../../models";
import { useStore as useProjectStore } from "../../Store";
import InvoicedButton from "../InvoicedButton";
import ItemTitle, { type ItemTitleProps } from "../ItemTitle";
import { Progress } from "../Progress";
import Uninvoiced from "../Uninvoiced";
import { OurWorkItemExpenseModal } from "./OurWorkItemExpenseModal";

const ADD_NEW_BUTTON_ID = "add-new-button";

type TOurWorkBudgetItem = OurWorkOverviewModel["budgetItems"][0];

function isTaskDone(task: TOurWorkBudgetItem["tasks"][0]): boolean {
  return (
    task.status.system_status === TaskSystemStatusEnum.Done &&
    (!task.subtasks ||
      task.subtasks.every(
        (subtask) => subtask.status.system_status === TaskSystemStatusEnum.Done,
      ))
  );
}

function filterAndSortTasks(
  tasks: TOurWorkBudgetItem["tasks"],
  showDoneTasks: boolean,
) {
  return tasks
    .filter((task) => (showDoneTasks ? true : !isTaskDone(task)))
    .toSorted((a, b) => Number(a.id) - Number(b.id));
}

type OurWorkItemProps = {
  item: TOurWorkBudgetItem;
  visibleBudgetItemIds: string[];
} & Pick<ItemTitleProps, "tabIndex">;

const OurWorkItem: FunctionComponent<
  React.PropsWithChildren<OurWorkItemProps>
> = ({ item, tabIndex, visibleBudgetItemIds }) => {
  const { taskFormModalStore, taskDetailModalStore, UIStore } = useStore();
  const store = useProjectStore();
  const [isDeadlineChanging, setIsDeadlineChanging] = useState(false);

  const filteredTasks = useMemo(() => {
    return filterAndSortTasks(item.tasks, item.showDoneTasks).map((task) => {
      return {
        ...task,
        subtasks: task.subtasks
          ? filterAndSortTasks(task.subtasks, item.showDoneTasks)
          : undefined,
      };
    });
  }, [item.tasks, item.showDoneTasks]);

  const expenseModal = useDisclosure();
  const { isOpen, onToggle, onClose, onOpen } = useDisclosure({
    defaultIsOpen: store.subRowsOpen.value,
  });

  useEffect(() => {
    if (store.subRowsOpen.value) {
      onOpen();
    } else {
      onClose();
    }
  }, [store.subRowsOpen.value]);

  const addTaskToBudgetItem = (task: SimpleTaskFragment) => {
    const budgetItem = store.getBudgetItem(task);
    if (budgetItem) {
      budgetItem.addTask(task);
      taskDetailModalStore.disclosureState.onOpen({
        id: task.id,
        onChange: (changedTask) => {
          if (!("stats" in changedTask)) return;
          updateTask(changedTask);
          store.refetchProject?.();
        },
        onDelete: removeTaskFromBudgetItem,
        onDuplicate: addTaskToBudgetItem,
        onTimeTrackingItemCreated: updateTrackedTime(task.id),
      });
    }
  };

  const updateTask = (
    task: SimpleTaskFragment | SimpleTaskForClientFragment,
  ) => {
    // NOTE: This screen is available only for internal users, so we can safely ignore scoped user
    // task fragment error
    if (!("stats" in task)) return;
    const budgetItem = store.getBudgetItem(task);
    if (!budgetItem) return;

    const allTasks: Array<SimpleTaskFragment> = [];

    for (const task of budgetItem.tasks) {
      allTasks.push(task);
      if (!task.subtasks) continue;
      for (const subtask of task.subtasks) {
        allTasks.push(subtask);
      }
    }

    const current = allTasks.find((i) => i.id === task.id);

    if (current) {
      budgetItem.updateTask({ ...current, ...task });
      UIStore.toast({
        status: "success",
        title: t`Status changed`,
      });
    }

    store.refetchProject?.();
  };

  const removeTaskFromBudgetItem = (task: TaskModel) => {
    const budgetItem = store.getBudgetItem(task);
    if (budgetItem) {
      budgetItem.removeTask(task.id);
      store.refetchProject?.();
    }
  };

  const updateTrackedTime =
    (taskId: Task["id"]) => (trackedTime: number | undefined) => {
      store.project?.ourWorkOverview.forEach((budgetItem) => {
        budgetItem.updateTrackedTime(taskId, trackedTime);
      });
      store.refetchProject?.();
    };

  const onAddTask = () => {
    taskFormModalStore.modalState.onOpen({
      isTemplate: store.project?.isTemplate,
      billable: store.project?.billable,
      projectId: store.project!.id,
      budgetItemId: item.id,
      parentTask: undefined,
      onSubmit: (task) => {
        const budgetItem = store.getBudgetItem(task);

        if (budgetItem) {
          budgetItem.addTask(task);
          store.refetchProject?.();
          taskDetailModalStore.disclosureState.onOpen({
            id: task.id,
            onChange: (changedTask) => {
              if (!("stats" in changedTask)) return;
              updateTask(changedTask);
              store.refetchProject?.();
            },
            onDelete: removeTaskFromBudgetItem,
            onDuplicate: addTaskToBudgetItem,
            onTimeTrackingItemCreated: updateTrackedTime(task.id),
          });
        }
      },
    });
  };

  const handleCreateSubtask = (subtask: InternalTaskType) => {
    store.refetchProject?.();
    taskDetailModalStore.disclosureState.onOpen({
      id: subtask.id,
      onChange: (changedTask) => {
        if (!("stats" in changedTask)) return;
        updateTask(changedTask);
        store.refetchProject?.();
      },
      onDelete: removeTaskFromBudgetItem,
      onDuplicate: addTaskToBudgetItem,
      onTimeTrackingItemCreated: updateTrackedTime(subtask.id),
    });
  };

  const onOpenCommunicationModal = (id: Task["id"]) => {
    taskDetailModalStore.disclosureState.onOpen({
      id,
      onChange: updateTask,
      onDelete: removeTaskFromBudgetItem,
      onDuplicate: addTaskToBudgetItem,
      onTimeTrackingItemCreated: updateTrackedTime(id),
    });
  };

  const handleChangeDeadline = async (deadline: Date | null) => {
    setIsDeadlineChanging(true);
    item.setDeadline({
      start: deadline ?? undefined,
      end: deadline ?? undefined,
    });
    await store.updateOurWorkOverviewBudgetItems();
    setIsDeadlineChanging(false);
  };

  const allVisibleIds = useMemo(() => {
    const ids: string[] = [];

    for (const task of filteredTasks) {
      ids.push(task.id);
      if (!task.subtasks?.length) continue;
      for (const subtask of task.subtasks) {
        ids.push(subtask.id);
      }
    }

    return ids;
  }, [filteredTasks]);

  const isChecked =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) =>
      store.taskListingBulkActionsStore.selectedIds.has(id),
    );

  const isIndeterminate =
    !isChecked &&
    allVisibleIds.length > 0 &&
    allVisibleIds.some((id) =>
      store.taskListingBulkActionsStore.selectedIds.has(id),
    );

  const handleSelectAll = () => {
    if (isChecked) {
      for (const task of item.tasks) {
        store.taskListingBulkActionsStore.selectedIds.delete(task.id);
        if (!task.subtasks?.length) continue;
        for (const subtask of task.subtasks) {
          store.taskListingBulkActionsStore.selectedIds.delete(subtask.id);
        }
      }
    } else {
      for (const id of allVisibleIds) {
        store.taskListingBulkActionsStore.selectedIds.add(id);
      }
    }
  };

  return (
    <Box data-group>
      <Grid
        alignItems="center"
        templateColumns={
          store.project?.isTemplate
            ? "0.5fr 0.5fr"
            : "minmax(auto, 0.575fr) 0.45fr 0.175fr 0.125fr"
        }
        px="4"
        py="3"
        borderColor="gray.300"
        borderBottomWidth={isOpen ? "none" : "1px"}
        bgColor={isOpen ? "teal.50" : "initial"}
      >
        <HStack spacing="2.5">
          <IconButton
            _hover={{ bgColor: isOpen ? "teal.200" : "gray.200" }}
            aria-label={t`toggle rows`}
            bgColor={isOpen ? "teal.100" : "initial"}
            colorScheme="teal"
            onClick={() => {
              onToggle();
            }}
            size="sm"
          >
            <Icon
              name="chevron-down"
              transform={isOpen ? "rotate(-180deg)" : "rotate(0)"}
              color={isOpen ? "teal.600" : "gray.500"}
            />
          </IconButton>
          <Checkbox
            isChecked={isChecked}
            isIndeterminate={isIndeterminate}
            onChange={handleSelectAll}
          />
          <Box maxW="300px">
            <ItemTitle
              item={item}
              tabIndex={tabIndex}
              onChangeDeadline={handleChangeDeadline}
              isDeadlineLoading={isDeadlineChanging}
            />
          </Box>
          {can("task_create_all") && (
            <Menu>
              <MenuButton
                as={Button}
                fontWeight="medium"
                _hover={{ bgColor: "unset", color: "purple.700" }}
                _groupHover={{ visibility: "visible" }}
                visibility="hidden"
                colorScheme="purple"
                id={ADD_NEW_BUTTON_ID}
                leftIcon={<Icon name="plus" />}
                size="xs"
                variant="outline"
              >
                <Trans>Task</Trans>
              </MenuButton>
              <MenuList>
                <MenuItem onClick={onAddTask}>
                  <Trans>Blank</Trans>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    store.handleOpenCreateTaskFromTemplateGroupModal(item.id);
                  }}
                >
                  <Trans>Task group</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </HStack>

        <Popover isLazy placement="bottom-start" trigger="hover">
          <PopoverTrigger>
            <Box w={{ base: "90%", lg: "75%" }}>
              <Progress
                item={item}
                onSpentBudgetClick={() => expenseModal.onOpen()}
                visibleBudgetItemIds={visibleBudgetItemIds}
              />
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              color="whiteAlpha.900"
              bg="grey.800"
              borderRadius="xl"
            >
              <OurWorkTooltipLabel
                stats={item.stats}
                isTemplate={store.project?.isTemplate ?? false}
              />
            </PopoverContent>
          </Portal>
        </Popover>

        {!store.project?.isTemplate && (
          <InvoicedButton item={item} w="fit-content" />
        )}

        {!store.project?.isTemplate && (
          <Text textAlign="end">
            <Uninvoiced
              value={item.stats.uninvoiced}
              isProBono={!store.project?.billable}
            />
          </Text>
        )}

        <OurWorkItemExpenseModal
          isOpen={expenseModal.isOpen}
          onClose={expenseModal.onClose}
          expenseStats={item.expenseStats}
          addQueryParams={{
            brandId: store.project?.brand.id,
            projectId: store.project?.id,
            budgetItemId: `${item.id}-${ExpenseBudgetItemTypeEnum.OurWorkBudgetItem}`,
          }}
        />
      </Grid>

      {can("task_read_all", "task_read_own") && (
        <Collapse animateOpacity in={isOpen}>
          <Box
            pr="4"
            pl="14"
            bg="teal.50"
            borderBottom="1px"
            borderBottomColor="gray.300"
          >
            {item.taskCount === 0 ? (
              <Text align="center" color="gray.600">{t`No tasks`}</Text>
            ) : (
              <React.Fragment>
                {filteredTasks.map((i) => {
                  const subtaskIds = i.subtasks?.map(({ id }) => id) ?? [];
                  const checkedSubtaskIds = subtaskIds.filter((id) =>
                    store.taskListingBulkActionsStore.isSelected(id),
                  );

                  return (
                    <TaskItemProject
                      key={i.id}
                      task={i}
                      isChecked={store.taskListingBulkActionsStore.isSelected(
                        i.id,
                      )}
                      isIndeterminate={store.taskListingBulkActionsStore.isIndeterminate(
                        i.id,
                        subtaskIds,
                      )}
                      checkedSubtaskIds={checkedSubtaskIds}
                      onCheck={(taskId) =>
                        store.taskListingBulkActionsStore.handleSelect(
                          taskId,
                          subtaskIds,
                        )
                      }
                      onCreateSubtask={handleCreateSubtask}
                      isProjectTemplate={store.project?.isTemplate}
                      onClick={(task) => {
                        onOpenCommunicationModal(task.id);
                      }}
                    />
                  );
                })}
              </React.Fragment>
            )}
            <Button
              my="4"
              opacity="0"
              _groupHover={{
                opacity: "1",
              }}
              transition="ease"
              isLoading={item.isDoneTasksLoading}
              onClick={async () => {
                if (item.showDoneTasks) {
                  item.setShowDoneTasks(false);
                  return;
                }

                await store.fetchDoneTasks(item.id);
                item.setShowDoneTasks(true);
              }}
              size="sm"
              variant="link"
            >
              {item.showDoneTasks ? (
                <Trans>Hide done tasks</Trans>
              ) : (
                <Text>
                  <Trans>Show done tasks</Trans>&nbsp;(
                  {item.doneTaskCount})
                </Text>
              )}
            </Button>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default observer(OurWorkItem);
